import { useState } from 'react';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import './AILoadBalancer.css';

const MOCK_PROVIDERS = [
  { id: 1, name: 'OpenAI GPT-4', status: 'active', latency: 245, requests: 12847, errorRate: 0.02, weight: 40 },
  { id: 2, name: 'Claude 3 Opus', status: 'active', latency: 312, requests: 8923, errorRate: 0.01, weight: 35 },
  { id: 3, name: 'Google Gemini', status: 'active', latency: 189, requests: 5621, errorRate: 0.03, weight: 20 },
  { id: 4, name: 'Mistral Large', status: 'degraded', latency: 567, requests: 2341, errorRate: 0.08, weight: 5 },
];

const AILoadBalancer = () => {
  const [selectedStrategy, setSelectedStrategy] = useState('weighted');

  const stats = [
    { label: 'Total Requests', value: '29,732', change: '23%', changeType: 'positive' },
    { label: 'Avg Latency', value: '278ms', change: '-12%', changeType: 'positive' },
    { label: 'Success Rate', value: '99.7%', change: '+0.2%', changeType: 'positive' },
    { label: 'Cost Saved', value: '$1,234', change: '18%', changeType: 'positive' },
  ];

  const getStatusClass = (status) => {
    return status === 'active' ? 'status-active' : 'status-degraded';
  };

  return (
    <div className="ai-lb-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">AI Load Balancer</h1>
          <p className="page-subtitle">Manage AI provider routing and failover</p>
        </div>
        <div className="page-actions">
          <Button variant="secondary" icon="settings">Configure</Button>
          <Button variant="primary" icon="plus">Add Provider</Button>
        </div>
      </div>

      <div className="stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Strategy Selection */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">Load Balancing Strategy</h3>
        </div>

        <div className="strategy-grid">
          <button
            className={`strategy-card ${selectedStrategy === 'weighted' ? 'active' : ''}`}
            onClick={() => setSelectedStrategy('weighted')}
          >
            <Icon name="bar-chart" size="lg" />
            <div className="strategy-name">Weighted</div>
            <div className="strategy-desc">Distribute based on configured weights</div>
          </button>

          <button
            className={`strategy-card ${selectedStrategy === 'latency' ? 'active' : ''}`}
            onClick={() => setSelectedStrategy('latency')}
          >
            <Icon name="zap" size="lg" />
            <div className="strategy-name">Lowest Latency</div>
            <div className="strategy-desc">Route to fastest responding provider</div>
          </button>

          <button
            className={`strategy-card ${selectedStrategy === 'cost' ? 'active' : ''}`}
            onClick={() => setSelectedStrategy('cost')}
          >
            <Icon name="dollar-sign" size="lg" />
            <div className="strategy-name">Cost Optimized</div>
            <div className="strategy-desc">Minimize cost while meeting SLA</div>
          </button>

          <button
            className={`strategy-card ${selectedStrategy === 'roundrobin' ? 'active' : ''}`}
            onClick={() => setSelectedStrategy('roundrobin')}
          >
            <Icon name="refresh" size="lg" />
            <div className="strategy-name">Round Robin</div>
            <div className="strategy-desc">Equal distribution across providers</div>
          </button>
        </div>
      </section>

      {/* Providers */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">AI Providers</h3>
          <Button variant="ghost" size="sm" icon="refresh">Refresh</Button>
        </div>

        <div className="providers-table-container">
          <table className="providers-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Status</th>
                <th>Latency</th>
                <th>Requests (24h)</th>
                <th>Error Rate</th>
                <th>Weight</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {MOCK_PROVIDERS.map((provider) => (
                <tr key={provider.id}>
                  <td className="provider-name">
                    <Icon name="brain" size="sm" />
                    {provider.name}
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(provider.status)}`}>
                      {provider.status}
                    </span>
                  </td>
                  <td className="latency-cell">
                    {provider.latency}ms
                    <div className="latency-bar">
                      <div
                        className="latency-fill"
                        style={{ width: `${Math.min(provider.latency / 6, 100)}%` }}
                      />
                    </div>
                  </td>
                  <td>{provider.requests.toLocaleString()}</td>
                  <td className={provider.errorRate > 0.05 ? 'text-danger' : 'text-success'}>
                    {(provider.errorRate * 100).toFixed(2)}%
                  </td>
                  <td>
                    <div className="weight-control">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={provider.weight}
                        className="weight-slider"
                        readOnly
                      />
                      <span className="weight-value">{provider.weight}%</span>
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Button variant="ghost" size="xs" icon="edit" iconOnly aria-label="Edit" />
                      <Button variant="ghost" size="xs" icon="ban" iconOnly aria-label="Disable" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Failover Rules */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">Failover Configuration</h3>
        </div>

        <div className="failover-settings">
          <div className="failover-item">
            <div className="failover-label">Max Retries</div>
            <input type="number" className="form-input small" value="3" readOnly />
          </div>
          <div className="failover-item">
            <div className="failover-label">Timeout (ms)</div>
            <input type="number" className="form-input small" value="5000" readOnly />
          </div>
          <div className="failover-item">
            <div className="failover-label">Circuit Breaker Threshold</div>
            <input type="number" className="form-input small" value="5" readOnly />
          </div>
          <div className="failover-item">
            <div className="failover-label">Recovery Time (s)</div>
            <input type="number" className="form-input small" value="30" readOnly />
          </div>
        </div>
      </section>
    </div>
  );
};

export default AILoadBalancer;
